import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useIncomeByOrigin } from '@/hooks/admin/AdminDashboardTab/useDashboardDetails';
import { formatCurrency, formatCompactCurrency } from '@/utils/shared/currencyUtils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

type IncomeByOriginChartProps = {
  year: number;
  month: number;
  propertyIds?: string[];
  organizationIds?: string[];
};

// Mapeo de colores por origen (basado en ORIGIN_DOT_COLORS)
const ORIGIN_CHART_COLORS: Record<string, string> = {
  "airbnb": "hsl(0, 73%, 60%)",           // red-400
  "airbnb.com": "hsl(0, 73%, 60%)",       // red-400
  "booking": "hsl(213, 94%, 68%)",        // blue-400
  "booking.com": "hsl(213, 94%, 68%)",    // blue-400
  "estancia corta": "hsl(var(--brand-primary))",
  "reservas directas": "hsl(173, 58%, 59%)", // teal-400
  "directo": "hsl(173, 58%, 59%)",        // teal-400
  "redes sociales": "hsl(142, 71%, 45%)", // green-500
  "referencia": "hsl(271, 76%, 53%)",     // purple-400
  "otro": "hsl(220, 9%, 46%)",            // gray-400
  "otros": "hsl(220, 9%, 46%)",           // gray-400 para categoría "Otros"
  "default": "hsl(220, 9%, 46%)",         // gray-500
};

const getOriginColor = (originName: string): string => {
  const normalized = originName.trim().toLowerCase();
  return ORIGIN_CHART_COLORS[normalized] || ORIGIN_CHART_COLORS["default"];
};

const IncomeByOriginChart = ({
  year,
  month,
  propertyIds,
  organizationIds,
}: IncomeByOriginChartProps) => {
  const { data: incomeByOrigin, isLoading } = useIncomeByOrigin({
    year,
    month,
    propertyIds,
    organizationIds,
    enabled: true,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Ingresos por Canal</CardTitle>
        </CardHeader>
        <CardContent className="px-2">
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!incomeByOrigin || incomeByOrigin.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Ingresos por Canal</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground text-center py-8">
            No hay datos de ingresos para mostrar en este período
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ingresos por Canal</CardTitle>
      </CardHeader>
      <CardContent className="px-2">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={incomeByOrigin}
            layout="vertical"
            margin={{ top: 5, right: 10, left: 5, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" opacity={0.3} />
            <XAxis 
              type="number" 
              tickFormatter={formatCompactCurrency}
              tickCount={5}
              axisLine={{ stroke: "hsl(var(--border))" }}
              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
            />
            <YAxis 
              type="category" 
              dataKey="origin_name" 
              width={100}
              axisLine={false}
              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
            />
            <Tooltip
              formatter={(value: number) => [formatCurrency(value), "Ingreso total"]}
              cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "8px",
                boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)"
              }}
            />
            <Bar 
              dataKey="total_income" 
              radius={[0, 12, 12, 0]}
              barSize={30}
            >
              {incomeByOrigin.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={getOriginColor(entry.origin_name)}
                  opacity={0.9}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default IncomeByOriginChart;
